import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
    FaArrowLeft, FaCamera, FaWeight,
    FaRulerVertical, FaSave, FaInfoCircle, FaMedal, FaIdCard
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { AppContext } from '../../context/AppContext';

const AddAthlete = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { uuid } = useParams();
    const { masterData } = useContext(AppContext);

    const isEditMode = !!uuid;

    const KYORUGI_FESTIVAL_BELTS = [
        'Putih',
        'Kuning',
        'Kuning Strip',
        'Hijau',
        'Hijau Strip'
    ];

    const POOMSAE_CATEGORY_OPTIONS = ['Individual', 'Freestyle', 'Pair', 'Team'];

    const normalizePoomsaeCategoryValue = (value) => {
        const raw = String(value || '').toLowerCase().trim();

        if (raw.includes('freestyle') || raw.includes('free style')) return 'Freestyle';
        if (raw.includes('pair') || raw.includes('pasangan') || raw.includes('ganda')) return 'Pair';
        if (raw.includes('team') || raw.includes('regu') || raw.includes('kelompok')) return 'Team';

        return 'Individual';
    };

    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [teams, setTeams] = useState([]);

    const defaultType = location.state?.preselectedType || 'Prestasi';
    const defaultCompType = location.state?.preselectedCompType || 'Kyorugi';

    const [formData, setFormData] = useState({
        name: '',
        gender: 'Lelaki',
        birth_year: '',
        team_id: location.state?.preselectedTeamId || '',
        type: defaultType,
        competition_type: defaultCompType,
        division: '',
        weight_class: '',
        declared_weight: '',
        height: '',
        poomsae_category: defaultCompType === 'Poomsae' ? 'Individual' : '',
        belt: ''
    });

    const [entryMembers, setEntryMembers] = useState({
        member1: '',
        member2: '',
        member3: ''
    });

    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const preselectedTeamName = location.state?.preselectedTeamName;

    // ==============================================================
    // HELPER PENGAMBIL DATA
    // ==============================================================
    const getConfig = () => {
        if (!masterData) return {};

        let data = masterData;

        if (data.result) data = data.result;
        else if (data.data) data = data.data;

        if (Array.isArray(data)) data = data[0];

        if (data?.config) {
            if (typeof data.config === 'string') {
                try { return JSON.parse(data.config); } catch (e) { return {}; }
            }
            return data.config;
        }

        return data || {};
    };

    const getBeltOptions = () => {
        const config = getConfig();
        return config.belt_levels || config.beltLevels || [];
    };

    const getPoomsaeBeltOptions = () => {
        const belts = getBeltOptions();
        return belts.length > 0 ? belts : [
            'Putih',
            'Kuning',
            'Kuning Strip',
            'Hijau',
            'Hijau Strip',
            'Biru',
            'Biru Strip',
            'Merah',
            'Merah Strip',
            'Poom',
            'Hitam'
        ];
    };

    const parseMembers = (members, fallbackName = '') => {
        if (Array.isArray(members)) {
            return members.map(item => String(item || '').trim()).filter(Boolean);
        }

        if (typeof members === 'string' && members.trim() !== '') {
            try {
                const parsed = JSON.parse(members);
                if (Array.isArray(parsed)) {
                    return parsed.map(item => String(item || '').trim()).filter(Boolean);
                }
            } catch (error) {
                return members
                    .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                    .map(item => String(item || '').trim())
                    .filter(Boolean);
            }
        }

        if (fallbackName) {
            return String(fallbackName)
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(item => String(item || '').trim())
                .filter(Boolean);
        }

        return [];
    };

    // 1. Hitung Divisi
    const calculateDivisionDynamic = (birthYear) => {
        const config = getConfig();
        if (!birthYear || birthYear.toString().length !== 4 || !config.divisions) return "";

        const eventYear = parseInt(config.event_year) || new Date().getFullYear();
        const age = eventYear - parseInt(birthYear);

        const foundDivision = config.divisions.find(div => {
            const min = parseInt(div.min_age) || 0;
            const max = parseInt(div.max_age) || 0;
            return age >= min && (max === 0 || age <= max);
        });

        return foundDivision ? foundDivision.name : "Tidak Layak (Usia)";
    };

    // 2. Ambil Opsi Kelas Tanding Berdasarkan Divisi
    const getFilteredWeightOptions = () => {
        const config = getConfig();
        const classesObj = config.weight_classes || config.weightClasses;

        if (!classesObj || !formData.division || !formData.gender) return [];

        const currentDiv = formData.division.toLowerCase().trim();
        const divKey = Object.keys(classesObj).find(k => k.toLowerCase().trim() === currentDiv);

        if (!divKey) return [];

        const divisionData = classesObj[divKey];
        const currentGender = formData.gender.toLowerCase().trim();

        let genderKey = Object.keys(divisionData).find(k => k.toLowerCase().trim() === currentGender);

        if (!genderKey) {
            if (['lelaki', 'laki-laki', 'l'].includes(currentGender)) {
                genderKey = Object.keys(divisionData).find(k => ['putra', 'male', 'pa', 'laki'].includes(k.toLowerCase().trim()));
            } else if (['perempuan', 'p'].includes(currentGender)) {
                genderKey = Object.keys(divisionData).find(k => ['putri', 'female', 'pi', 'cewek'].includes(k.toLowerCase().trim()));
            }
        }

        return genderKey ? (divisionData[genderKey] || []) : [];
    };

    // ==============================================================
    // EFFECTS
    // ==============================================================
    useEffect(() => {
        if (!formData.team_id) {
            api.get('/teams', { params: { limit: 1000 } })
                .then(res => setTeams(res.data.result || []))
                .catch(err => console.error(err));
        }
    }, [formData.team_id]);

    useEffect(() => {
        if (isEditMode) {
            setIsFetching(true);

            api.get(`/athletes/${uuid}`).then(res => {
                const data = res.data;
                const members = parseMembers(data.entry_members, data.name);

                setFormData({
                    name: data.name || '',
                    gender: data.gender === 'Campuran' ? 'Lelaki' : (data.gender || 'Lelaki'),
                    birth_year: data.birth_year || '',
                    team_id: data.team_id || '',
                    type: data.type || 'Prestasi',
                    division: data.division || '',
                    competition_type: data.competition_type || 'Kyorugi',
                    weight_class: data.weight_class || '',
                    declared_weight: data.declared_weight || '',
                    height: data.height || '',
                    poomsae_category: data.poomsae_category || (data.competition_type === 'Poomsae' ? 'Individual' : ''),
                    belt: data.belt || ''
                });

                setEntryMembers({
                    member1: members[0] || data.name || '',
                    member2: members[1] || '',
                    member3: members[2] || ''
                });

                if (data.image) setPreviewUrl(`${process.env.REACT_APP_BACKEND}/uploads/athletes/${data.image}`);
                setIsFetching(false);
            }).catch(() => {
                toast.error("Gagal memuat data atlet.");
                navigate(-1);
            });
        }
    }, [isEditMode, uuid, navigate]);

    useEffect(() => {
        const birthYearStr = formData.birth_year?.toString() || '';
        const config = getConfig();

        if (birthYearStr.length === 4 && config.divisions) {
            setFormData(prev => ({ ...prev, division: calculateDivisionDynamic(birthYearStr) || '' }));
        } else if (birthYearStr.length < 4) {
            setFormData(prev => ({ ...prev, division: '' }));
        }

        if (formData.competition_type === 'Poomsae') {
            setFormData(prev => ({
                ...prev,
                weight_class: '',
                declared_weight: '',
                height: '',
                poomsae_category: prev.poomsae_category || 'Individual'
            }));
        } else if (formData.competition_type === 'Kyorugi') {
            setFormData(prev => ({
                ...prev,
                poomsae_category: '',
                belt: prev.type === 'Festival' ? prev.belt : ''
            }));
        }

        if (formData.type === 'Festival') {
            setFormData(prev => ({ ...prev, weight_class: '' }));
        } else if (formData.type === 'Prestasi') {
            setFormData(prev => ({
                ...prev,
                declared_weight: '',
                height: '',
                belt: prev.competition_type === 'Poomsae' ? prev.belt : ''
            }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.birth_year, formData.competition_type, formData.type, masterData]);

    // ==============================================================
    // HANDLERS
    // ==============================================================
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const nextValue = name === 'poomsae_category'
                ? normalizePoomsaeCategoryValue(value)
                : value;
            const nextData = { ...prev, [name]: nextValue };

            if (name === 'poomsae_category') {
                if (nextValue === 'Pair') {
                    nextData.gender = 'Campuran';
                } else if (prev.gender === 'Campuran') {
                    nextData.gender = 'Lelaki';
                }
            }

            return nextData;
        });
    };

    const handleEntryMemberChange = (key, value) => {
        setEntryMembers(prev => ({ ...prev, [key]: value }));
    };

    const handleCompetitionTypeChange = (competitionType) => {
        setFormData(prev => ({
            ...prev,
            competition_type: competitionType,
            poomsae_category: competitionType === 'Poomsae' ? 'Individual' : '',
            weight_class: '',
            declared_weight: '',
            height: '',
            belt: ''
        }));

        setEntryMembers({
            member1: '',
            member2: '',
            member3: ''
        });
    };

    const handleParticipantTypeChange = (participantType) => {
        setFormData(prev => ({
            ...prev,
            type: participantType,
            weight_class: '',
            declared_weight: '',
            height: '',
            belt: '',
            poomsae_category: prev.competition_type === 'Poomsae'
                ? (participantType === 'Prestasi' ? (prev.poomsae_category || 'Individual') : '')
                : ''
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            if (file.size > 2 * 1024 * 1024) return toast.warning("Ukuran foto maksimal 2MB");
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const getActivePoomsaeCategory = (formElement = null) => {
        if (formData.competition_type !== 'Poomsae') return '';

        const formCategory = formElement?.elements?.poomsae_category?.value;
        return normalizePoomsaeCategoryValue(formCategory || formData.poomsae_category);
    };

    const getFinalEntryMembers = (poomsaeCategory = getActivePoomsaeCategory()) => {
        const isPoomsae = formData.competition_type === 'Poomsae';
        const category = poomsaeCategory;

        if (isPoomsae && category === 'Pair') {
            return [
                entryMembers.member1,
                entryMembers.member2
            ].map(item => String(item || '').trim()).filter(Boolean);
        }

        if (isPoomsae && category === 'Team') {
            return [
                entryMembers.member1,
                entryMembers.member2,
                entryMembers.member3
            ].map(item => String(item || '').trim()).filter(Boolean);
        }

        return [
            formData.name
        ].map(item => String(item || '').trim()).filter(Boolean);
    };

    const validateFormBeforeSubmit = (poomsaeCategory = getActivePoomsaeCategory()) => {
        const isKyorugi = formData.competition_type === 'Kyorugi';
        const isPoomsae = formData.competition_type === 'Poomsae';
        const isPrestasi = formData.type === 'Prestasi';
        const isFestival = formData.type === 'Festival';

        if ((formData.division || '').includes("Tidak Layak")) {
            toast.error("Usia atlet tidak memenuhi syarat atau belum diisi dengan benar.");
            return false;
        }

        if (isKyorugi && isFestival) {
            if (!formData.declared_weight) {
                toast.error("Berat badan wajib diisi untuk Kyorugi Festival.");
                return false;
            }

            if (!formData.height) {
                toast.error("Tinggi badan wajib diisi untuk Kyorugi Festival.");
                return false;
            }

            if (!formData.belt) {
                toast.error("Sabuk wajib dipilih untuk Kyorugi Festival.");
                return false;
            }

            if (!KYORUGI_FESTIVAL_BELTS.includes(formData.belt)) {
                toast.error("Kyorugi Festival hanya boleh untuk sabuk Putih sampai Hijau Strip.");
                return false;
            }
        }

        if (isPoomsae && isPrestasi && poomsaeCategory === 'Pair') {
            const members = getFinalEntryMembers(poomsaeCategory);

            if (members.length !== 2) {
                toast.error("Poomsae Pair wajib mengisi 2 nama atlet.");
                return false;
            }
        }

        if (isPoomsae && isPrestasi && poomsaeCategory === 'Team') {
            const members = getFinalEntryMembers(poomsaeCategory);

            if (members.length !== 3) {
                toast.error("Poomsae Team wajib mengisi 3 nama atlet.");
                return false;
            }

            if (!['Lelaki', 'Perempuan'].includes(formData.gender)) {
                toast.error("Poomsae Team wajib memilih Team Putra atau Team Putri.");
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const finalPoomsaeCategory = getActivePoomsaeCategory(e.currentTarget);

        if (!validateFormBeforeSubmit(finalPoomsaeCategory)) return;

        setIsLoading(true);

        try {
            const finalMembers = getFinalEntryMembers(finalPoomsaeCategory);

            let finalName = formData.name;
            let finalGender = formData.gender;

            if (formData.competition_type === 'Poomsae' && finalPoomsaeCategory === 'Pair') {
                finalName = finalMembers.join(' / ');
                finalGender = 'Campuran';
            }

            if (formData.competition_type === 'Poomsae' && finalPoomsaeCategory === 'Team') {
                finalName = finalMembers.join(' / ');
            }

            const finalPayload = {
                ...formData,
                poomsae_category: formData.competition_type === 'Poomsae' ? finalPoomsaeCategory : '',
                name: finalName,
                gender: finalGender
            };

            const data = new FormData();

            Object.keys(finalPayload).forEach(key => {
                if (
                    finalPayload[key] !== null &&
                    finalPayload[key] !== undefined &&
                    finalPayload[key] !== ''
                ) {
                    data.append(key, finalPayload[key]);
                }
            });

            if (finalMembers.length > 0) {
                data.append('entry_members', JSON.stringify(finalMembers));
            }

            if (imageFile) data.append('image', imageFile);

            const configParams = { headers: { 'Content-Type': 'multipart/form-data' } };

            if (isEditMode) {
                await api.put(`/athletes/${uuid}`, data, configParams);
                toast.success("Data atlet diperbarui!");
            } else {
                await api.post('/athletes', data, configParams);
                toast.success("Atlet berhasil ditambahkan!");
            }

            navigate(-1);
        } catch (error) {
            toast.error(error.response?.data?.msg || "Gagal menyimpan data.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!masterData || isFetching) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

    const isKyorugi = formData.competition_type === 'Kyorugi';
    const isPoomsae = formData.competition_type === 'Poomsae';
    const isPrestasi = formData.type === 'Prestasi';
    const isFestival = formData.type === 'Festival';

    const isPoomsaePair = isPoomsae && isPrestasi && formData.poomsae_category === 'Pair';
    const isPoomsaeTeam = isPoomsae && isPrestasi && formData.poomsae_category === 'Team';
    const isPoomsaeGroupEntry = isPoomsaePair || isPoomsaeTeam;

    return (
        <div className="container-fluid p-0 animate-fade-in">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <button onClick={() => navigate(-1)} className="btn btn-light rounded-circle shadow-sm p-2" style={{ width: '45px', height: '45px' }}>
                        <FaArrowLeft className="text-muted" />
                    </button>
                    <div>
                        <h3 className="fw-bold text-dark m-0">{isEditMode ? 'Edit Data Atlet' : 'Tambah Atlet Baru'}</h3>
                        <p className="text-muted small mb-0">Lengkapi formulir pendaftaran atlet di bawah ini.</p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="row g-4">
                    <div className="col-lg-8">
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-body p-4">

                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <div className="bg-success bg-opacity-10 p-2 rounded text-success"><FaMedal /></div>
                                    <h6 className="fw-bold mb-0">Klasifikasi Pertandingan</h6>
                                </div>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted">Kategori Peserta</label>
                                        <div className="row g-2">
                                            {['Prestasi', 'Festival'].map(cat => (
                                                <div className="col-6" key={cat}>
                                                    <button type="button"
                                                        className={`btn w-100 py-2 fw-bold transition-all ${formData.type === cat ? 'btn-warning text-white shadow-sm' : 'btn-outline-warning'}`}
                                                        onClick={() => handleParticipantTypeChange(cat)}>
                                                        {cat.toUpperCase()}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted">Jenis Pertandingan</label>
                                        <div className="row g-2">
                                            {['Kyorugi', 'Poomsae'].map(type => (
                                                <div className="col-6" key={type}>
                                                    <button type="button"
                                                        className={`btn w-100 py-2 fw-bold transition-all ${formData.competition_type === type ? 'btn-primary shadow-sm' : 'btn-outline-primary'}`}
                                                        onClick={() => handleCompetitionTypeChange(type)}>
                                                        {type.toUpperCase()}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <hr className="border-light mb-4" />

                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <div className="bg-primary bg-opacity-10 p-2 rounded text-primary"><FaIdCard /></div>
                                    <h6 className="fw-bold mb-0">Informasi Identitas & Spesifikasi</h6>
                                </div>

                                <div className="row g-3">
                                    {!isPoomsaeGroupEntry && (
                                        <div className="col-md-12">
                                            <label className="form-label fw-bold small text-muted">Nama Lengkap</label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control bg-light border-0 py-2"
                                                placeholder="Masukkan nama sesuai akta kelahiran"
                                                required
                                                value={formData.name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    )}

                                    {isPoomsaePair && (
                                        <>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold small text-muted">Nama Atlet Putra</label>
                                                <input
                                                    type="text"
                                                    className="form-control bg-light border-0 py-2"
                                                    placeholder="Masukkan nama atlet putra"
                                                    required
                                                    value={entryMembers.member1}
                                                    onChange={(e) => handleEntryMemberChange('member1', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold small text-muted">Nama Atlet Putri</label>
                                                <input
                                                    type="text"
                                                    className="form-control bg-light border-0 py-2"
                                                    placeholder="Masukkan nama atlet putri"
                                                    required
                                                    value={entryMembers.member2}
                                                    onChange={(e) => handleEntryMemberChange('member2', e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {isPoomsaeTeam && (
                                        <>
                                            <div className="col-md-4">
                                                <label className="form-label fw-bold small text-muted">Nama Atlet 1</label>
                                                <input
                                                    type="text"
                                                    className="form-control bg-light border-0 py-2"
                                                    placeholder="Nama atlet 1"
                                                    required
                                                    value={entryMembers.member1}
                                                    onChange={(e) => handleEntryMemberChange('member1', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label fw-bold small text-muted">Nama Atlet 2</label>
                                                <input
                                                    type="text"
                                                    className="form-control bg-light border-0 py-2"
                                                    placeholder="Nama atlet 2"
                                                    required
                                                    value={entryMembers.member2}
                                                    onChange={(e) => handleEntryMemberChange('member2', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label fw-bold small text-muted">Nama Atlet 3</label>
                                                <input
                                                    type="text"
                                                    className="form-control bg-light border-0 py-2"
                                                    placeholder="Nama atlet 3"
                                                    required
                                                    value={entryMembers.member3}
                                                    onChange={(e) => handleEntryMemberChange('member3', e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted">Tim / Kontingen</label>
                                        {preselectedTeamName ? (
                                            <input type="text" className="form-control bg-light fw-bold border-0 py-2" value={preselectedTeamName} readOnly />
                                        ) : (
                                            <select name="team_id" className="form-select bg-light border-0 py-2" required value={formData.team_id} onChange={handleChange}>
                                                <option value="">-- Pilih Tim Kontingen --</option>
                                                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                            </select>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted">
                                            {isPoomsaePair ? 'Jenis Kelamin' : isPoomsaeTeam ? 'Jenis Team' : 'Jenis Kelamin'}
                                        </label>

                                        {isPoomsaePair ? (
                                            <input
                                                type="text"
                                                className="form-control bg-light border-0 py-2 fw-bold"
                                                value="Campuran / Pair Lelaki dan Wanita"
                                                readOnly
                                            />
                                        ) : (
                                            <select name="gender" className="form-select bg-light border-0 py-2" value={formData.gender} onChange={handleChange}>
                                                <option value="Lelaki">{isPoomsaeTeam ? 'Team Putra' : 'Laki-laki'}</option>
                                                <option value="Perempuan">{isPoomsaeTeam ? 'Team Putri' : 'Perempuan'}</option>
                                            </select>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted">Tahun Lahir</label>
                                        <input
                                            type="number"
                                            name="birth_year"
                                            className="form-control bg-light border-0 py-2"
                                            placeholder={`Contoh: ${(getConfig().event_year || new Date().getFullYear()) - 15}`}
                                            required
                                            value={formData.birth_year}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted">Divisi Usia (Otomatis)</label>
                                        <input
                                            type="text"
                                            className={`form-control fw-bold border-0 py-2 bg-white ${(formData.division || '').includes('Tidak') ? 'text-danger' : 'text-success'}`}
                                            value={formData.division || "Isi tahun lahir..."}
                                            readOnly
                                        />
                                    </div>

                                    {(isPoomsae && isFestival) && (
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold small text-muted">Tingkat Sabuk</label>
                                            <select name="belt" className="form-select bg-light border-0 py-2" required value={formData.belt} onChange={handleChange}>
                                                <option value="">-- Pilih Sabuk --</option>
                                                {getPoomsaeBeltOptions().map(belt => (
                                                    <option key={belt} value={belt}>{belt}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {(isPoomsae && isPrestasi) && (
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold small text-muted">Kategori Poomsae</label>
                                            <select name="poomsae_category" className="form-select bg-light border-0 py-2" required value={normalizePoomsaeCategoryValue(formData.poomsae_category)} onChange={handleChange}>
                                                {POOMSAE_CATEGORY_OPTIONS.map(category => (
                                                    <option key={category} value={category}>
                                                        {category === 'Individual'
                                                            ? 'Individual (Perorangan)'
                                                            : category === 'Pair'
                                                                ? 'Pair (Berpasangan)'
                                                                : category === 'Team'
                                                                    ? 'Team (Beregu)'
                                                                    : 'Freestyle'}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {(isKyorugi && isFestival) && (
                                        <>
                                            <div className="col-md-4">
                                                <label className="form-label fw-bold small text-muted">Berat Badan (Kg)</label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0"><FaWeight className="text-muted" /></span>
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        name="declared_weight"
                                                        className="form-control bg-light border-start-0 py-2"
                                                        placeholder="0.0"
                                                        required
                                                        value={formData.declared_weight}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-bold small text-muted">Tinggi Badan (cm)</label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0"><FaRulerVertical className="text-muted" /></span>
                                                    <input
                                                        type="number"
                                                        name="height"
                                                        className="form-control bg-light border-start-0 py-2"
                                                        placeholder="0"
                                                        required
                                                        value={formData.height}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-bold small text-muted">Sabuk</label>
                                                <select
                                                    name="belt"
                                                    className="form-select bg-light border-0 py-2"
                                                    required
                                                    value={formData.belt}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">-- Pilih Sabuk --</option>
                                                    {KYORUGI_FESTIVAL_BELTS.map(belt => (
                                                        <option key={belt} value={belt}>{belt}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </>
                                    )}

                                    {(isKyorugi && isPrestasi) && (
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold small text-muted">Kelas Tanding (Weight Class)</label>
                                            <select name="weight_class" className="form-select bg-light border-0 py-2" value={formData.weight_class} onChange={handleChange} required>
                                                <option value="">-- Pilih Kelas Tanding --</option>
                                                {getFilteredWeightOptions().map(wc => <option key={wc} value={wc}>{wc}</option>)}
                                            </select>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4">
                        <div className="card border-0 shadow-sm rounded-4 mb-4 text-center p-4">
                            <h6 className="fw-bold text-dark text-start mb-3">Foto Identitas Atlet</h6>
                            <label className="cursor-pointer position-relative group">
                                <div className="rounded-4 border border-2 border-dashed d-flex align-items-center justify-content-center bg-light transition-all overflow-hidden"
                                    style={{ height: '250px', borderColor: '#dee2e6' }}>
                                    {previewUrl ? (
                                        <img src={previewUrl} alt="Preview" className="w-100 h-100 object-fit-cover rounded-4" />
                                    ) : (
                                        <div className="text-center p-3">
                                            <FaCamera size={45} className="text-muted opacity-25 mb-2" />
                                            <p className="text-muted small m-0">Klik untuk upload foto formal (Max 2MB)</p>
                                        </div>
                                    )}
                                </div>
                                <input type="file" className="d-none" accept="image/*" onChange={handleFileChange} />
                            </label>
                        </div>

                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-body p-4">
                                <button type="submit" className="btn btn-primary w-100 py-3 rounded-pill fw-bold shadow-lg mb-3" disabled={isLoading || (formData.division || '').includes("Tidak Layak")}>
                                    {isLoading ? <div className="spinner-border spinner-border-sm text-white"></div> : <span><FaSave className="me-2" /> {isEditMode ? 'Perbarui Data' : 'Simpan Atlet'}</span>}
                                </button>

                                {(isKyorugi && isFestival) && (
                                    <div className="alert alert-warning border-0 m-0 small d-flex gap-2">
                                        <FaInfoCircle className="mt-1 flex-shrink-0" />
                                        <span>Bagan Kyorugi Festival diatur otomatis berdasarkan berat, tinggi badan, dan hanya untuk sabuk Putih sampai Hijau Strip.</span>
                                    </div>
                                )}

                                {(isPoomsaePair || isPoomsaeTeam) && (
                                    <div className="alert alert-info border-0 mt-3 mb-0 small d-flex gap-2">
                                        <FaInfoCircle className="mt-1 flex-shrink-0" />
                                        <span>Pair/Team tetap dihitung sebagai satu entry agar biaya pendaftaran tidak dobel.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default AddAthlete;
